import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";
import { Document, Page, pdfjs } from "react-pdf";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/** =========================
 *  Gestos / constantes
 * ========================= */
const TAP_MOVE_TOLERANCE_PX = 8;
const TAP_MAX_MS = 260;

const LONGPRESS_EDIT_MS = 1000;
const LONGPRESS_DELETE_MS = 5000;

const MIN_W = 40;
const MIN_H = 20;

const LAYOUT_KEY = "_layout_v1";
const FIRMAS_KEY = "_firmas_v1";

const PDF_ZOOM = 1.8;

/** =========================
 *  Layout fijo (coords PDF)
 * ========================= */
const FIXED_LAYOUT = [
  { kind: "ot", x: 375, y: 308, w: 320, h: 26, type: "text", locked: true },
  { kind: "cliente", x: 203, y: 361, w: 360, h: 26, type: "text", locked: true },
  { kind: "centro", x: 1113, y: 360, w: 360, h: 26, type: "text", locked: true },
  { kind: "fecha", x: 1061, y: 309, w: 300, h: 26, type: "text", locked: true },
  {
    kind: "firmawm",
    x: 1298,
    y: 2179,
    w: 255,
    h: 104,
    type: "signature",
    tipo: "responsable_wemotion",
    locked: true,
  },
  {
    kind: "firmacliente",
    x: 387,
    y: 2183,
    w: 381,
    h: 98,
    type: "signature",
    tipo: "responsable_cliente",
    locked: true,
  },
];

const FIXED_KINDS = FIXED_LAYOUT.map((f) => f.kind);

const uid = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

function normalizeField(f) {
  return {
    uid: String(f.uid || uid()),
    kind: String(f.kind || "texto"),
    type: f.type || (String(f.kind || "").startsWith("firma") ? "signature" : "text"),
    tipo: f.tipo,
    x: Number(f.x || 0),
    y: Number(f.y || 0),
    w: Math.max(MIN_W, Number(f.w || 160)),
    h: Math.max(MIN_H, Number(f.h || 32)),
    value: String(f.value || ""),
    locked: Boolean(f.locked),
  };
}

function upsertFixedFields(existingFields, valuesByKind) {
  const byKind = new Map(existingFields.map((f) => [f.kind, f]));
  const out = [...existingFields.filter((f) => !FIXED_KINDS.includes(f.kind))];

  for (const fixed of FIXED_LAYOUT) {
    const prev = byKind.get(fixed.kind);

    const next = normalizeField({
      uid: prev?.uid || uid(),
      kind: fixed.kind,
      type: fixed.type,
      tipo: fixed.tipo,
      x: fixed.x,
      y: fixed.y,
      w: fixed.w,
      h: fixed.h,
      locked: true,
      value: fixed.type === "text" ? (valuesByKind[fixed.kind] ?? prev?.value ?? "") : "",
    });

    out.push(next);
  }

  out.sort((a, b) => {
    const ia = FIXED_KINDS.includes(a.kind) ? FIXED_KINDS.indexOf(a.kind) : 999;
    const ib = FIXED_KINDS.includes(b.kind) ? FIXED_KINDS.indexOf(b.kind) : 999;
    return ia - ib;
  });

  return out;
}

/** =========================
 *  Dialog de firma
 * ========================= */
function SignatureDialog({ open, title, onClose, onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  const getPos = (e) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    const scaleX = c.width / r.width;
    const scaleY = c.height / r.height;
    return {
      x: (e.clientX - r.left) * scaleX,
      y: (e.clientY - r.top) * scaleY,
    };
  };

  const onDown = (e) => {
    drawing.current = true;
    last.current = getPos(e);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const p = getPos(e);

    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";

    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();

    last.current = p;
  };

  const onUp = () => {
    drawing.current = false;
  };

  const save = () => {
    const c = canvasRef.current;
    if (!c) return;
    onSave?.(c.toDataURL("image/png"));
  };

  useEffect(() => {
    if (!open) return;
    setTimeout(() => clear(), 0);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || "Firmar"}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.2)",
            borderRadius: 1,
            overflow: "hidden",
            touchAction: "none",
            bgcolor: "white",
          }}
        >
          <canvas
            ref={canvasRef}
            width={900}
            height={300}
            style={{ width: "100%", height: 240, display: "block" }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onPointerLeave={onUp}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={clear}>Limpiar</Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>
          Guardar firma
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** =========================================================
 *  Página principal
 * ======================================================= */
export const ParteTrabajoPage = () => {
  const { ticajeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const navState = (location && location.state) || {};

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [parte, setParte] = useState(null);

  /** ===== URL absoluta para /api/uploads ===== */
  const API_BASE = Global.url || "/api/";
  const SERVER_BASE = useMemo(() => {
    if (/^https?:\/\//i.test(API_BASE)) return API_BASE.replace(/\/api\/?$/, "");
    return "";
  }, [API_BASE]);

  const toAbs = useCallback(
    (p) => {
      if (!p || typeof p !== "string") return null;
      if (p.startsWith("data:image/")) return p;
      if (p.startsWith("http")) return p;
      if (p.startsWith("/")) return `${SERVER_BASE}${p}`;
      return p;
    },
    [SERVER_BASE]
  );

  /** ===== PDF sizing ===== */
  const containerRef = useRef(null);
  const [pageSize, setPageSize] = useState({ width: 1, height: 1 });
  const [containerWidth, setContainerWidth] = useState(900);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return; // ✅ evita error observe(null)

    const ro = new ResizeObserver(() => {
      const cur = containerRef.current;
      if (cur) setContainerWidth(cur.clientWidth || 900);
    });

    ro.observe(el);
    setContainerWidth(el.clientWidth || 900);

    return () => ro.disconnect();
  }, []);

  const renderWidth = useMemo(() => Math.max(1, Math.floor(containerWidth * PDF_ZOOM)), [containerWidth]);
  const scale = useMemo(() => renderWidth / (pageSize.width || 1), [renderWidth, pageSize.width]);

  /** ===== Auth headers ===== */
  const token = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  /** ===== PDF memo ===== */
  const pdfUrl = "/Intervencion.pdf";
  const file = useMemo(() => ({ url: pdfUrl }), []);
  const options = useMemo(() => ({ enableXfa: false }), []);

  /** ===== Cargar/crear parte ===== */
  const cargarOcrearParte = useCallback(async () => {
    const idNum = parseInt(ticajeId, 10);
    if (Number.isNaN(idNum)) {
      setError("ticajeId inválido");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const resGet = await fetch(`${Global.url}partes-trabajo/ticaje-finalizado/${idNum}`, { headers });

      if (resGet.ok) {
        const data = await resGet.json();
        setParte(data);
        return;
      }

      if (resGet.status === 404) {
        const resPost = await fetch(`${Global.url}partes-trabajo/ticaje-finalizado/${idNum}`, {
          method: "POST",
          headers,
        });

        if (!resPost.ok) {
          const t = await resPost.text().catch(() => "");
          throw new Error(t || "No se pudo crear el parte");
        }

        const created = await resPost.json();
        const parteCreada = created?.data || created;
        setParte(parteCreada);
        toast.success("Parte creado");
        return;
      }

      const txt = await resGet.text().catch(() => "");
      throw new Error(txt || "Error al cargar el parte");
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar/crear el parte");
    } finally {
      setLoading(false);
    }
  }, [ticajeId, headers, toast]);

  useEffect(() => {
    cargarOcrearParte();
  }, [cargarOcrearParte]);

  /** ===== Datos fallback ===== */
  const datosConAuto = useMemo(() => {
    const datos = parte?.datos || {};
    const out = { ...(datos || {}) };
    out.ot_texto = out.ot_texto || (datos?.ot?.titulo || (datos?.ot?.id ? `OT ${datos.ot.id}` : ""));
    out.cliente_texto = out.cliente_texto || (datos?.cliente?.nombre || "");
    out.centro_texto = out.centro_texto || (datos?.centro?.nombre || "");
    out.fecha_texto = out.fecha_texto || "";
    return out;
  }, [parte?.datos]);

  /** ===== Valores fijos (state > datos) ===== */
  const fixedValuesByKind = useMemo(
    () => ({
      ot: navState.ot ?? datosConAuto.ot_texto ?? "",
      cliente: navState.cliente ?? datosConAuto.cliente_texto ?? "",
      centro: navState.centro ?? datosConAuto.centro_texto ?? "",
      fecha: navState.fecha ?? datosConAuto.fecha_texto ?? "",
    }),
    [navState.ot, navState.cliente, navState.centro, navState.fecha, datosConAuto]
  );

  /** ===== Layout ===== */
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (!parte) return;
    const raw = parte?.datos?.[LAYOUT_KEY];
    const existing = Array.isArray(raw) ? raw.map(normalizeField) : [];
    const withFixed = upsertFixedFields(existing, fixedValuesByKind);

    setFields(withFixed);
    setParte((prev) => ({
      ...prev,
      datos: { ...(prev?.datos || {}), [LAYOUT_KEY]: withFixed },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parte?.id]);

  useEffect(() => {
    if (!parte) return;
    setFields((prev) => upsertFixedFields(prev, fixedValuesByKind));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedValuesByKind.ot, fixedValuesByKind.cliente, fixedValuesByKind.centro, fixedValuesByKind.fecha]);

  useEffect(() => {
    if (!parte) return;
    setParte((prev) => ({
      ...prev,
      datos: { ...(prev?.datos || {}), [LAYOUT_KEY]: fields },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  /** ===== Firmas ===== */
  const firmasLocal = useMemo(() => parte?.datos?.[FIRMAS_KEY] || {}, [parte?.datos]);

  const getFirmaTipo = (field) =>
    field?.tipo || (field?.kind === "firmacliente" ? "responsable_cliente" : "responsable_wemotion");

  const getFirmaSrc = (field) => toAbs(firmasLocal?.[getFirmaTipo(field)] || null);

  const [firmaField, setFirmaField] = useState(null);

  const guardarFirma = async (firmaDataUrl) => {
    if (!parte?.id || !firmaField) return;
    const tipo = getFirmaTipo(firmaField);

    try {
      const res = await fetch(`${Global.url}partes-trabajo/${parte.id}/firmas`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          tipo,
          firma_data_url: firmaDataUrl,
          nombre_firmante: null,
          cargo_firmante: null,
          email_firmante: null,
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error guardando firma");
      }

      const out = await res.json().catch(() => ({}));
      const ruta = out?.data?.ruta_firma || null;

      const nuevosFirmas = { ...(parte?.datos?.[FIRMAS_KEY] || {}), [tipo]: ruta || firmaDataUrl };
      const datosActualizados = { ...(parte?.datos || {}), [FIRMAS_KEY]: nuevosFirmas };

      setParte((prev) => ({
        ...prev,
        estado: tipo === "responsable_cliente" ? "firmado_cliente" : prev?.estado,
        datos: datosActualizados,
      }));

      // Persistir en JSONB para que reaparezca sin join
      await fetch(`${Global.url}partes-trabajo/${parte.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ datos: datosActualizados }),
      }).catch(() => {});

      toast.success("Firma guardada");
      setFirmaField(null);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo guardar la firma");
    }
  };

  /** ===== Editor texto (long press) ===== */
  const [editFieldId, setEditFieldId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const editField = useMemo(
    () => (editFieldId ? fields.find((f) => f.uid === editFieldId) : null),
    [editFieldId, fields]
  );

  const openEditorFor = useCallback(
    (fieldId) => {
      const f = fields.find((x) => x.uid === fieldId);
      if (!f || f.locked || f.type === "signature") return;
      setEditFieldId(fieldId);
      setEditValue(f.value || "");
    },
    [fields]
  );

  const closeEditor = () => {
    setEditFieldId(null);
    setEditValue("");
  };

  const saveEditor = () => {
    if (!editFieldId) return;
    setFields((prev) => prev.map((f) => (f.uid === editFieldId ? { ...f, value: editValue } : f)));
    closeEditor();
  };

  /** ===== Gestos ===== */
  const containerPointerDownRef = useRef(new Map());
  const activeFieldPointersRef = useRef(new Map());
  const gestureRef = useRef({ fieldId: null, mode: null, pinch: null });

  const longPressRef = useRef({
    fieldId: null,
    editReady: false,
    editTimer: null,
    deleteTimer: null,
    startClient: null,
    moved: false,
  });

  const clearLongPress = () => {
    if (longPressRef.current.editTimer) clearTimeout(longPressRef.current.editTimer);
    if (longPressRef.current.deleteTimer) clearTimeout(longPressRef.current.deleteTimer);
    longPressRef.current = { fieldId: null, editReady: false, editTimer: null, deleteTimer: null, startClient: null, moved: false };
  };

  const pdfPointFromClient = (clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const b = el.getBoundingClientRect();
    return { x: (clientX - b.left) / scale, y: (clientY - b.top) / scale };
  };

  const onContainerPointerDown = (e) => {
    if (e.target?.dataset?.field === "1") return;
    containerPointerDownRef.current.set(e.pointerId, {
      startClient: { x: e.clientX, y: e.clientY },
      startAt: Date.now(),
    });
  };

  const onContainerPointerUp = (e) => {
    const down = containerPointerDownRef.current.get(e.pointerId);
    containerPointerDownRef.current.delete(e.pointerId);
    if (!down) return;

    const dx = Math.abs(e.clientX - down.startClient.x);
    const dy = Math.abs(e.clientY - down.startClient.y);
    const dt = Date.now() - down.startAt;

    if (dx <= TAP_MOVE_TOLERANCE_PX && dy <= TAP_MOVE_TOLERANCE_PX && dt <= TAP_MAX_MS) {
      const p = pdfPointFromClient(e.clientX, e.clientY);
      const nf = normalizeField({
        uid: uid(),
        kind: "texto",
        type: "text",
        x: Math.max(0, p.x),
        y: Math.max(0, p.y),
        w: 220,
        h: 34,
        value: "",
        locked: false,
      });
      setFields((prev) => [...prev, nf]);
    }
  };

  const deleteField = useCallback(
    (fieldId) => {
      const f = fields.find((x) => x.uid === fieldId);
      if (!f || f.locked) return;
      setFields((prev) => prev.filter((x) => x.uid !== fieldId));
      if (editFieldId === fieldId) closeEditor();
    },
    [fields, editFieldId]
  );

  const onFieldPointerDown = (fieldId, e) => {
    e.stopPropagation();
    const f = fields.find((x) => x.uid === fieldId);
    if (!f) return;

    if (f.type === "signature") {
      setFirmaField(f);
      return;
    }
    if (f.locked) return;

    const pt = pdfPointFromClient(e.clientX, e.clientY);

    activeFieldPointersRef.current.set(e.pointerId, {
      fieldId,
      pointerType: e.pointerType,
      startClient: { x: e.clientX, y: e.clientY },
      startPdf: pt,
      startRectSnapshot: { x: f.x, y: f.y, w: f.w, h: f.h },
      currentClient: { x: e.clientX, y: e.clientY },
    });

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    clearLongPress();
    longPressRef.current.fieldId = fieldId;
    longPressRef.current.startClient = { x: e.clientX, y: e.clientY };
    longPressRef.current.moved = false;

    longPressRef.current.editTimer = setTimeout(() => {
      if (gestureRef.current.mode === "pinch") return;
      if (longPressRef.current.moved) return;
      longPressRef.current.editReady = true;
      openEditorFor(fieldId);
    }, LONGPRESS_EDIT_MS);

    longPressRef.current.deleteTimer = setTimeout(() => {
      if (gestureRef.current.mode === "pinch") return;
      if (longPressRef.current.moved) return;
      deleteField(fieldId);
      clearLongPress();
    }, LONGPRESS_DELETE_MS);
  };

  const onPointerMove = (e) => {
    const ap = activeFieldPointersRef.current.get(e.pointerId);
    if (!ap) return;

    ap.currentClient = { x: e.clientX, y: e.clientY };

    if (longPressRef.current.startClient) {
      const mdx = Math.abs(e.clientX - longPressRef.current.startClient.x);
      const mdy = Math.abs(e.clientY - longPressRef.current.startClient.y);
      if (mdx > TAP_MOVE_TOLERANCE_PX || mdy > TAP_MOVE_TOLERANCE_PX) {
        longPressRef.current.moved = true;
      }
    }

    const fieldId = ap.fieldId;

    const same = [];
    for (const [, v] of activeFieldPointersRef.current.entries()) {
      if (v.fieldId === fieldId) same.push(v);
    }

    // pinch resize
    if (same.length >= 2) {
      clearLongPress();
      const [p1, p2] = same;

      if (gestureRef.current.fieldId !== fieldId || gestureRef.current.mode !== "pinch" || !gestureRef.current.pinch) {
        const dx0 = Math.abs(p2.startPdf.x - p1.startPdf.x) || 1;
        const dy0 = Math.abs(p2.startPdf.y - p1.startPdf.y) || 1;

        const r0 = p1.startRectSnapshot;
        const cx0 = r0.x + r0.w / 2;
        const cy0 = r0.y + r0.h / 2;

        gestureRef.current = {
          fieldId,
          mode: "pinch",
          pinch: { dx0, dy0, rect0: { ...r0 }, c0: { x: cx0, y: cy0 } },
        };
      }

      const pinch = gestureRef.current.pinch;
      if (!pinch) return;

      const p1now = pdfPointFromClient(p1.currentClient.x, p1.currentClient.y);
      const p2now = pdfPointFromClient(p2.currentClient.x, p2.currentClient.y);

      const dx1 = Math.abs(p2now.x - p1now.x) || 1;
      const dy1 = Math.abs(p2now.y - p1now.y) || 1;

      const sx = dx1 / pinch.dx0;
      const sy = dy1 / pinch.dy0;

      const nw = Math.max(MIN_W, pinch.rect0.w * sx);
      const nh = Math.max(MIN_H, pinch.rect0.h * sy);

      const nx = Math.max(0, pinch.c0.x - nw / 2);
      const ny = Math.max(0, pinch.c0.y - nh / 2);

      setFields((prev) => prev.map((f) => (f.uid === fieldId ? { ...f, x: nx, y: ny, w: nw, h: nh } : f)));
      return;
    }

    // move
    clearLongPress();

    const dxPdf = (e.clientX - ap.startClient.x) / scale;
    const dyPdf = (e.clientY - ap.startClient.y) / scale;

    // mouse shift resize
    if (ap.pointerType === "mouse" && e.shiftKey) {
      const nw = Math.max(MIN_W, ap.startRectSnapshot.w + dxPdf);
      const nh = Math.max(MIN_H, ap.startRectSnapshot.h + dyPdf);
      setFields((prev) => prev.map((f) => (f.uid === fieldId ? { ...f, w: nw, h: nh } : f)));
      return;
    }

    setFields((prev) =>
      prev.map((f) =>
        f.uid === fieldId
          ? {
              ...f,
              x: Math.max(0, ap.startRectSnapshot.x + dxPdf),
              y: Math.max(0, ap.startRectSnapshot.y + dyPdf),
            }
          : f
      )
    );
  };

  const onPointerUpOrCancel = (e) => {
    containerPointerDownRef.current.delete(e.pointerId);
    activeFieldPointersRef.current.delete(e.pointerId);

    // reset gesture if no pointers
    let still = false;
    for (const [, v] of activeFieldPointersRef.current.entries()) {
      if (v.fieldId === gestureRef.current.fieldId) {
        still = true;
        break;
      }
    }
    if (!still) gestureRef.current = { fieldId: null, mode: null, pinch: null };

    clearLongPress();
  };

  /** ===== Guardar parte ===== */
  const onGuardar = async () => {
    if (!parte?.id) return;
    setGuardando(true);
    try {
      const res = await fetch(`${Global.url}partes-trabajo/${parte.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ datos: parte.datos }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error al guardar");
      }

      const out = await res.json();
      const updated = out?.data || out;
      setParte(updated);
      toast.success("Guardado");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  /** ===== Render ===== */
  if (loading) {
    return (
      <Box className="appContainer appContainer--wide">
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography>Cargando…</Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="appContainer appContainer--wide">
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                Volver
              </Button>
              <Button variant="contained" onClick={cargarOcrearParte}>
                Reintentar
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Parte de intervención
              </Typography>
              <Chip size="small" label={parte?.estado || "-"} />
            </Stack>

            <Button variant="contained" startIcon={<SaveIcon />} onClick={onGuardar} disabled={guardando}>
              {guardando ? "Guardando…" : "Guardar"}
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box
              ref={containerRef}
              sx={{ position: "relative", width: "100%", touchAction: "none" }}
              onPointerDown={onContainerPointerDown}
              onPointerUp={onContainerPointerUp}
              onPointerMove={onPointerMove}
              onPointerCancel={onPointerUpOrCancel}
            >
              <Document
                file={file}
                options={options}
                loading={
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Cargando PDF…</Typography>
                  </Stack>
                }
                error={<Alert severity="error" sx={{ m: 2 }}>No se pudo cargar el PDF.</Alert>}
              >
                <Page
                  pageNumber={1}
                  width={renderWidth}
                  onLoadSuccess={(p) => setPageSize({ width: p.width, height: p.height })}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {fields.map((f) => {
                const left = f.x * scale;
                const top = f.y * scale;
                const width = f.w * scale;
                const height = f.h * scale;

                const fontPdf = f.locked ? 9.5 : 10.8;
                const fontPx = Math.max(13, Math.round(fontPdf * scale));
                const fontFamily = "Arial Narrow, Helvetica Neue Condensed, Roboto Condensed, Arial, sans-serif";
                const fontWeight = f.locked ? 650 : 750;

                const isSignature = f.type === "signature";
                const firmaSrc = isSignature ? getFirmaSrc(f) : null;

                return (
                  <Box
                    key={f.uid}
                    data-field="1"
                    onPointerDown={(e) => onFieldPointerDown(f.uid, e)}
                    onPointerUp={onPointerUpOrCancel}
                    onPointerCancel={onPointerUpOrCancel}
                    sx={{
                      position: "absolute",
                      left,
                      top,
                      width,
                      height,
                      border: "1px solid rgba(0,0,0,0.18)",
                      bgcolor: "rgba(255,255,255,0.35)",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                      overflow: "hidden",
                      userSelect: "none",
                      cursor: isSignature ? "pointer" : f.locked ? "default" : "move",
                    }}
                  >
                    {isSignature ? (
                      firmaSrc ? (
                        <Box component="img" src={firmaSrc} alt={`Firma ${f.kind}`} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      ) : null
                    ) : (
                      <Typography
                        sx={{
                          width: "100%",
                          fontFamily,
                          fontWeight,
                          fontSize: `${fontPx}px`,
                          lineHeight: 1.1,
                          letterSpacing: "0.2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "rgba(0,0,0,0.88)",
                          opacity: f.value ? 1 : 0.6,
                        }}
                      >
                        {f.value || ""}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      </Stack>

      {/* Editor texto */}
      <Dialog open={Boolean(editFieldId)} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>Editar</DialogTitle>
        <DialogContent>
          {editField?.locked || editField?.type === "signature" ? (
            <Alert severity="info">Este campo no es editable.</Alert>
          ) : (
            <TextField value={editValue} onChange={(e) => setEditValue(e.target.value)} fullWidth multiline minRows={4} autoFocus />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancelar</Button>
          <Button variant="contained" onClick={saveEditor} disabled={Boolean(editField?.locked || editField?.type === "signature")}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Firma */}
      <SignatureDialog
        open={Boolean(firmaField)}
        title={firmaField?.tipo === "responsable_cliente" ? "Firma cliente" : "Firma técnico (WeMotion)"}
        onClose={() => setFirmaField(null)}
        onSave={guardarFirma}
      />
    </Box>
  );
};

export default ParteTrabajoPage;
